import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BContainer, BRow } from "@/lib/typed-bootstrap";
import { RoleTypeEnum } from "@/api/common-types";
import { IRoleByOrgItem, watchRolesByOrg } from "@/models/roles";
import { OrgModule } from "@/store/modules/org";
import { UserModule } from "@/store/modules/user";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import Table from "@/components/shared/table/Table.component";
import { ListDataManager } from "@/utils/list-data-manager";
import { IQueryListRolesByOrgArgs } from "@/generated/types";
import { IItem } from "@/components/shared/table/types";
import { TableEditingState } from "@/components/shared/table/table-editing-state";
import { TableSelectionState } from "@/components/shared/table/table-selection-state";
import { SelectedDataExtractor } from "@/components/shared/table/selected-data-extractor";
import TableActions from "@/components/shared/table/TableActions.component";
import { deleteRoleMutation } from "@/api/role";
import styles from "./OrgUserManagement.view.module.scss";

@Component
export default class OrgUserManagement extends UserPermissions {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  private errorMessage = "";
  private showErrorMessage = false;

  protected get listDataManager() {
    return Vue.observable(
      new ListDataManager<
        { items: IRoleByOrgItem[]; nextToken?: string | null },
        IQueryListRolesByOrgArgs
      >((variables) => {
        return watchRolesByOrg(variables);
      }),
    );
  }

  private tableFields = [
    {
      key: "userEmail",
      label: this.$t("org_users_table_header_email").toString(),
    },
    { key: "type", label: this.$t("org_users_table_header_role").toString() },
    {
      key: "lastSignInAt",
      label: this.$t("org_users_table_header_signin").toString(),
    },
  ];

  @Watch("orgId", { immediate: true })
  async onOrgChange() {
    if (this.orgId) {
      this.listDataManager.subscribeToList({
        orgId: this.orgId,
      });
    }
  }

  private onInvite(invitee?: {
    email?: string;
    role?: string;
    roleId?: string;
  }): void {
    try {
      if (this.canManageUsers) {
        this.$router.push({
          name: "org-users-manage",
          params: {
            orgId: this.orgId,
            action: invitee?.email ? "edit" : "invite",
          },
          ...(invitee?.email && { query: invitee }),
        });
      } else {
        throw new Error(this.$t("error_user_mgt_can_manage").toString());
      }
    } catch (err) {
      this.showError((err as Error).message);
    }
  }

  async onDeleteEntries(): Promise<void> {
    if (!this.canManageUsers) {
      return;
    }

    try {
      const selectedRows = [...this.tableSelectionState.selectedRows.values()];
      const toDelete = selectedRows.map(
        (index) => this.listDataManager.data[index].id,
      );

      const isDeletingCurrentUser = toDelete.some((id) =>
        this.roleIdBelongsToCurrentUser(id),
      );
      const isDeletingOwnerWhileNotBeingOwner = toDelete.some((id) => {
        const role = this.listDataManager.data.find((item) => item.id === id);
        return role?.type === RoleTypeEnum.OWNER && !this.isOwner;
      });

      if (isDeletingCurrentUser) {
        throw new Error(this.$t("error_user_mgt_cant_remove_self").toString());
      }

      if (isDeletingOwnerWhileNotBeingOwner) {
        throw new Error(this.$t("error_user_mgt_cant_remove_owner").toString());
      }

      await Promise.all(
        toDelete.map((id) => deleteRoleMutation({ input: { id } })),
      );
    } catch (err) {
      this.showError((err as Error).message);
    }
  }

  private onEditTableEntry(index: number): void {
    const item = this.listDataManager.data[index];
    this.onInvite({
      email: item.userEmail,
      role: item.type,
      roleId: item.id,
    });
  }

  private roleIdBelongsToCurrentUser(roleId: string): boolean {
    return UserModule.roles[OrgModule.id]?.id === roleId;
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.showErrorMessage = true;
  }

  private selectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    false,
    false,
  );

  private get tableSelectionState() {
    return new TableSelectionState(true, false);
  }

  private get tableEditingState() {
    return Vue.observable(new TableEditingState(this.tableFields));
  }

  render() {
    return (
      <BContainer class="default__view-setup">
        <BAlert
          show={this.showErrorMessage}
          variant="danger"
          data-testid="user-management-error"
        >
          {this.errorMessage}
        </BAlert>

        <BRow class="actions__section">
          <div class="default__section-header">
            {this.$t("org_users_title")}{" "}
            <span class="default__color-accent">
              | {this.listDataManager.data.length} {this.$t("global_total")}
            </span>
          </div>
        </BRow>

        <BRow class="default__sub-section">
          <TableActions
            class={styles.tableActions}
            withDelete
            canAddItems={true}
            canDeleteItems={
              this.canManageUsers && this.tableSelectionState.hasAnyRowSelection
            }
            onAddEntry={() => this.onInvite()}
            onDeleteEntries={this.onDeleteEntries}
          />
          <Table
            fields={this.tableFields}
            items={this.listDataManager.data as unknown as IItem[]}
            tableSelectionState={this.tableSelectionState}
            tableEditingState={this.tableEditingState}
            withSelectionColumn
            withEditColumn
            selectedDataExtractor={this.selectedDataExtractor}
            hasMoreItems={!!this.listDataManager.listVariables?.nextToken}
            paginationState={this.listDataManager.paginationState}
            onPaginationChange={this.listDataManager.onPaginationChange}
            onEditTableEntry={this.onEditTableEntry}
          />
        </BRow>
      </BContainer>
    );
  }
}
